import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, doc, updateDoc, deleteDoc, query, orderBy
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, MenuItem, Divider,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';

const SECTION_TYPES = [
  { key: 'intro',    label: 'Inledning',          hint: 'Bakgrund och syfte med offerten' },
  { key: 'scope',    label: 'Uppdragsbeskrivning', hint: 'Vad uppdraget/tjänsten omfattar' },
  { key: 'approach', label: 'Metod & genomförande',hint: 'Hur vi arbetar och levererar' },
  { key: 'team',     label: 'Teamet',              hint: 'Presentera konsulter/medarbetare' },
  { key: 'pricing',  label: 'Prislista',           hint: 'Timpriser, fastpris eller licenser' },
  { key: 'references', label: 'Referensprojekt',   hint: 'Relevanta referensuppdrag' },
  { key: 'terms',    label: 'Villkor',              hint: 'Betalningsvillkor, avtal m.m.' },
  { key: 'custom',   label: 'Fritt block',          hint: 'Valfritt innehåll' },
];

const OFFER_TYPES = [
  { key: 'konsult-team',   label: 'Konsultteam' },
  { key: 'konsult-cv',     label: 'Konsult-CV' },
  { key: 'fastpris',       label: 'Fastpris' },
  { key: 'lopande',        label: 'Löpande arvode' },
  { key: 'produkt',        label: 'Produkt/SaaS' },
  { key: 'kombination',    label: 'Kombination' },
];

function newSection(type) {
  return {
    id: Date.now().toString(),
    type,
    title: SECTION_TYPES.find(s => s.key === type)?.label || type,
    defaultContent: '',
    required: false,
  };
}

const EMPTY_FORM = {
  name: '', departmentId: '', type: 'konsult-team',
  description: '', sections: []
};

export default function OfferTemplates() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null); // template id being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [addSectionType, setAddSectionType] = useState('intro');

  const load = useCallback(async () => {
    setLoading(true);
    const [tmplSnap, deptSnap] = await Promise.all([
      getDocs(query(collection(db, 'offerTemplates'), orderBy('createdAt', 'desc'))),
      getDocs(collection(db, 'departments')),
    ]);
    setTemplates(tmplSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialog(true);
  }

  function openEdit(tmpl) {
    setEditing(tmpl.id);
    setForm({
      name: tmpl.name || '',
      departmentId: tmpl.departmentId || '',
      type: tmpl.type || 'konsult-team',
      description: tmpl.description || '',
      sections: tmpl.sections || [],
    });
    setDialog(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    const data = {
      ...form,
      updatedAt: new Date(),
    };
    if (editing) {
      await updateDoc(doc(db, 'offerTemplates', editing), data);
    } else {
      await addDoc(collection(db, 'offerTemplates'), {
        ...data,
        createdBy: currentUser.uid,
        createdAt: new Date(),
      });
    }
    setSaving(false);
    setDialog(false);
    load();
  }

  async function handleDelete(id) {
    if (!window.confirm('Ta bort mallen?')) return;
    await deleteDoc(doc(db, 'offerTemplates', id));
    setTemplates(prev => prev.filter(t => t.id !== id));
  }

  function addSection() {
    setForm(f => ({
      ...f,
      sections: [...f.sections, newSection(addSectionType)]
    }));
  }

  function removeSection(secId) {
    setForm(f => ({ ...f, sections: f.sections.filter(s => s.id !== secId) }));
  }

  function updateSection(secId, field, value) {
    setForm(f => ({
      ...f,
      sections: f.sections.map(s => s.id === secId ? { ...s, [field]: value } : s)
    }));
  }

  function moveSection(idx, dir) {
    setForm(f => {
      const arr = [...f.sections];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return f;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...f, sections: arr };
    });
  }

  const grouped = departments.map(d => ({
    dept: d,
    templates: templates.filter(t => t.departmentId === d.id)
  })).filter(g => g.templates.length > 0);
  const unassigned = templates.filter(t => !t.departmentId);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/offers')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Offertmallar</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {templates.length} mallar
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
          Ny mall
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary" gutterBottom>
              Inga mallar ännu.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
              sx={{ mt: 1, bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
              Skapa första mallen
            </Button>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {[...grouped.map(g => ({ label: g.dept.name, templates: g.templates })),
              ...(unassigned.length ? [{ label: 'Utan affärsområde', templates: unassigned }] : [])
            ].map(group => (
              <Box key={group.label}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1, display: 'block' }}>
                  {group.label}
                </Typography>
                <Grid container spacing={2}>
                  {group.templates.map(tmpl => {
                    const typeLabel = OFFER_TYPES.find(t => t.key === tmpl.type)?.label || tmpl.type;
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={tmpl.id}>
                        <Paper sx={{ p: 2.5, height: '100%', display: 'flex',
                          flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start',
                            justifyContent: 'space-between', mb: 1 }}>
                            <Box>
                              <Typography fontWeight="bold">{tmpl.name}</Typography>
                              <Chip label={typeLabel} size="small"
                                sx={{ fontSize: '0.62rem', height: 18, mt: 0.5,
                                  bgcolor: '#1a1a1a', color: 'white' }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => openEdit(tmpl)}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton size="small"
                                onClick={() => handleDelete(tmpl.id)}
                                sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                          {tmpl.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              {tmpl.description}
                            </Typography>
                          )}
                          <Box sx={{ mt: 'auto' }}>
                            <Typography variant="caption" color="text.disabled">
                              {(tmpl.sections || []).length} sektioner:
                              {' '}{(tmpl.sections || []).map(s => s.title).join(', ')}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Container>

      {/* Template editor dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon sx={{ color: '#FF6B00' }} />
            <Typography fontWeight="bold">
              {editing ? 'Redigera mall' : 'Ny offertmall'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Mallnamn *" size="small"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth label="Affärsområde" size="small"
                value={form.departmentId}
                onChange={e => setForm(f => ({ ...f, departmentId: e.target.value }))}>
                <MenuItem value="">– Välj AO –</MenuItem>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth label="Typ" size="small"
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {OFFER_TYPES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Beskrivning (visas inte i offert)" size="small"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Sektioner
          </Typography>

          {form.sections.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Inga sektioner ännu.
            </Typography>
          ) : (
            <Box sx={{ mb: 1.5 }}>
              {form.sections.map((sec, idx) => (
                <Accordion key={sec.id} disableGutters elevation={0}
                  sx={{ border: '1px solid', borderColor: 'divider', mb: 0.5, borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
                      <Typography variant="body2" fontWeight={600}>{sec.title}</Typography>
                      <Chip label={SECTION_TYPES.find(s => s.key === sec.type)?.label || sec.type}
                        size="small"
                        sx={{ fontSize: '0.6rem', height: 18 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}
                      onClick={e => e.stopPropagation()}>
                      <IconButton size="small" disabled={idx === 0}
                        onClick={() => moveSection(idx, -1)}>
                        <span style={{ fontSize: 12 }}>↑</span>
                      </IconButton>
                      <IconButton size="small" disabled={idx === form.sections.length - 1}
                        onClick={() => moveSection(idx, 1)}>
                        <span style={{ fontSize: 12 }}>↓</span>
                      </IconButton>
                      <IconButton size="small"
                        onClick={() => removeSection(sec.id)}
                        sx={{ color: 'error.main', opacity: 0.6 }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Rubrik i offert" size="small"
                          value={sec.title}
                          onChange={e => updateSection(sec.id, 'title', e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Standardinnehåll (förifyllt vid skapande)"
                          size="small" multiline rows={3}
                          value={sec.defaultContent}
                          onChange={e => updateSection(sec.id, 'defaultContent', e.target.value)}
                          placeholder={SECTION_TYPES.find(s => s.key === sec.type)?.hint || ''} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField select size="small" value={addSectionType}
              onChange={e => setAddSectionType(e.target.value)} sx={{ minWidth: 200 }}>
              {SECTION_TYPES.map(s => (
                <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
              ))}
            </TextField>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={addSection}
              sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
              Lägg till sektion
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || !form.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Sparar...' : 'Spara mall'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
